import React from 'react';
import { Users, Eye } from 'lucide-react';
import type { Wine } from '@/types';

interface ShareBadgeProps {
  wine: Wine;
  currentUserId: string;
  size?: 'sm' | 'md';
  iconOnly?: boolean;
}

export const ShareBadge: React.FC<ShareBadgeProps> = ({ wine, currentUserId, size = 'md', iconOnly = false }) => {
  const isOwner = wine.ownerId === currentUserId || wine.userId === currentUserId;
  const myAccess = wine.sharedWith?.find(s => s.userId === currentUserId);

  // Vino di mia proprietà NON condiviso - non mostrare badge
  if (isOwner && !wine.isShared) {
    return null;
  }

  const iconSize = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3';
  const iconOnlySize = 'w-4 h-4';

  // Vino di mia proprietà condiviso (io sono owner)
  if (isOwner && wine.isShared && wine.sharedWith && wine.sharedWith.length > 0) {
    if (iconOnly) {
      return (
        <div className="p-1.5 rounded-full bg-blue-100/90 backdrop-blur-sm shadow-sm" title={`Condiviso con ${wine.sharedWith.length}`}>
          <Users className={`${iconOnlySize} text-blue-800`} />
        </div>
      );
    }
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${
          size === 'sm' ? 'text-[10px]' : 'text-xs'
        } font-semibold bg-blue-100 text-blue-800`}
      >
        <Users className={iconSize} />
        Condiviso con {wine.sharedWith.length}
      </span>
    );
  }

  // Vino condiviso con me - sola lettura
  if (!isOwner && myAccess?.type === 'read-only') {
    if (iconOnly) {
      return (
        <div className="p-1.5 rounded-full bg-cyan-100/90 backdrop-blur-sm shadow-sm" title="Solo lettura">
          <Eye className={`${iconOnlySize} text-cyan-800`} />
        </div>
      );
    }
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${
          size === 'sm' ? 'text-[10px]' : 'text-xs'
        } font-semibold bg-cyan-100 text-cyan-800`}
      >
        <Eye className={iconSize} />
        Solo lettura
      </span>
    );
  }

  // Vino condiviso con me - accesso completo
  if (!isOwner && myAccess?.type === 'full') {
    if (iconOnly) {
      return (
        <div className="p-1.5 rounded-full bg-green-100/90 backdrop-blur-sm shadow-sm" title="Condiviso">
          <Users className={`${iconOnlySize} text-green-800`} />
        </div>
      );
    }
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${
          size === 'sm' ? 'text-[10px]' : 'text-xs'
        } font-semibold bg-green-100 text-green-800`}
      >
        <Users className={iconSize} />
        Condiviso
      </span>
    );
  }

  return null;
};
